import { Area } from '@app/domain/Area'
import { Booking } from '@app/domain/Booking'
import { BookingChangedMetadata } from '@app/features/shared/components/booking/booking.component'
import {
    BookingDetailsViewModel,
    BookingDetailsViewModelDelegate,
} from '@app/features/shared/components/booking-details/booking-details-view-model'
import { BookingReason } from '../../../../domain/BookingReason'
import { BookingReasonChoice } from '../../../bookings/components/booking-reason-choice'
import { BookingEventChoice } from '../../../bookings/components/booking-event-choice'
import { Event } from '@app/domain/VenueEvent'
import { BookingService } from '@services/booking.service'
import {
    BookingStartEditComponent,
} from '@app/features/bookings/components/booking-start-edit/booking-start-edit.component'
import { Business } from '@app/domain/Business'
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren,
} from '@angular/core'
import { ContextService } from '@services/context.service'
import { Customer } from '@app/domain/Customer'
import { DateTime } from 'luxon'
import { DateTimePipe } from '../../pipes/date-time.pipe'
import { DiaryPreferencesService } from '@app/features/bookings/services/diary-preferences.service'
import {
    ElementChoiceSelectComponent,
} from '@app/features/shared/components/element-choice-select/element-choice-select.component'
import { Helper } from '@app/helpers/utilities/helper'
import { InputComponent, InputType } from '@app/features/shared/components/input/input.component'
import { PartySizeChoice } from '@app/features/bookings/components/party-size-choice'
import { ModalComponent } from '@app/features/shared/components/modal/modal.component'
import { ModelCloningService } from '@services/model-cloning.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Observable, Subject, delay, finalize, mergeMap, of, takeUntil, tap } from 'rxjs'
import { Table } from '@app/domain/Table'
import { TableNamePipe } from '@app/features/shared/pipes/table-name.pipe'
import {
    TableSelectionComponent,
} from '@app/features/bookings/components/table-selection/table-selection.component'
import { TextareaComponent } from '@app/features/shared/components/textarea/textarea.component'
import { TimeElementChoice } from '@app/features/bookings/components/time-element-choice'
import { ToastBuilder } from '@app/domain/ToastBuilder'
import { ToastService } from '@services/toast.service'
import { ToastType } from '@app/domain/Toast'
import { Venue } from '@app/domain/Venue'
import { VenueSchedule } from '@app/domain/VenueSchedule'

@Component({
    selector: 'app-booking-details',
    templateUrl: './booking-details.component.html',
    styleUrls: ['./booking-details.component.sass'],
})
export class BookingDetailsComponent implements
    OnInit,
    OnDestroy,
    BookingDetailsViewModelDelegate {

    @Input() business!: Business
    @Input() venue!: Venue
    @Input() booking!: Booking
    @Input() otherBookings!: Booking[]
    @Input() schedule!: VenueSchedule | null
    @Output() bookingChanged = new EventEmitter<[Booking, Partial<BookingChangedMetadata>]>()
    @Output() customerSelected = new EventEmitter<Customer>()
    area?: Area
    viewModel!: BookingDetailsViewModel
    bookingTimeViewStartingSelection?: TimeElementChoice
    bookingReasonStartingSelection?: BookingReasonChoice
    bookingEventStartingSelection?: BookingEventChoice
    partySizeStartingSelection?: PartySizeChoice

    @ViewChild('changeEndTime', { static: true })
        bookingTimeView!: ElementChoiceSelectComponent<Date>
    @ViewChild('changeBookingReason', { static: true })
        bookingReasonView!: ElementChoiceSelectComponent<BookingReason | null>
    @ViewChild('changeBookingEvent', { static: true })
        bookingEventView!: ElementChoiceSelectComponent<Event | null>
    @ViewChild('customerMessage', { static: true })
        customerMessageView!: TextareaComponent
    @ViewChild('merchantNotes', { static: true })
        merchantNotesView!: TextareaComponent
    @ViewChild('changePartySize', { static: true })
        partySizeView!: ElementChoiceSelectComponent<number>
    @ViewChildren('textQuestionInput')
        textQuestionInputs!: QueryList<InputComponent>
    private onDestroy = new Subject<void>()

    constructor(
        private bookingService: BookingService,
        private contextService: ContextService,
        private datePipe: DateTimePipe,
        private diaryPreferencesService: DiaryPreferencesService,
        private modalService: NgbModal,
        private modelCloningService: ModelCloningService,
        private tableNamePipe: TableNamePipe,
        private toastService: ToastService
    ) { }

    ngOnInit() {
        this.area = this.venue.areaBookingIsIn(this.booking) || undefined
        this.viewModel = new BookingDetailsViewModel(
            this.venue,
            this.booking,
            this.diaryPreferencesService.getHidePrivateNotes(),
            this
        )
        this.bookingTimeViewStartingSelection =
            new TimeElementChoice(this.booking.end, this.venue.timeZone, this.datePipe)
        this.partySizeStartingSelection = new PartySizeChoice(this.booking.size)
        if (this.booking.reasonId) {
            const reason = this.venue.reasonWithId(this.booking.reasonId)
            this.bookingReasonStartingSelection = new BookingReasonChoice(reason)
        } else {
            this.bookingReasonStartingSelection = new BookingReasonChoice(null)
        }
        if (this.booking.eventId) {
            const event = this.venue.eventWithId(this.booking.eventId)
            this.bookingEventStartingSelection = new BookingEventChoice(event)
        } else {
            this.bookingEventStartingSelection = new BookingEventChoice(null)
        }
        this.bookingChanged
            .pipe(
                takeUntil(this.onDestroy)
            )
            .subscribe(([booking, _]) => {
                this.booking = booking
                this.area = this.venue.areaBookingIsIn(this.booking)!
                this.viewModel = new BookingDetailsViewModel(
                    this.venue,
                    this.booking,
                    this.diaryPreferencesService.getHidePrivateNotes(),
                    this
                )
                this.bookingTimeViewStartingSelection =
                    new TimeElementChoice(this.booking.end, this.venue.timeZone, this.datePipe)
                this.partySizeStartingSelection = new PartySizeChoice(this.booking.size)
            })
    }

    ngOnDestroy() {
        this.onDestroy.next()
        this.onDestroy.complete()
    }

    get tableTurnaroundInterval(): number {
        return this.area?.tableTurnaroundIntervalForPartySize(this.booking.size) ?? 0
    }

    showChangeBookingStartTimeOptions() {
        if (this.schedule === null) {
            return
        }
        const modal = this.modalService.open(BookingStartEditComponent, {
            size: 'lg',
            scrollable: true,
        })
        const component = modal.componentInstance as BookingStartEditComponent
        component.venue = this.venue
        component.booking = this.booking
        modal.closed
            .pipe(
                takeUntil(this.onDestroy)
            )
            .subscribe((booking: Booking) => {
                this.booking = booking
                this.bookingChanged.emit([booking, {}])
                this.showSuccess(
                    `Booking start time changed to ${this.datePipe.transform(booking.start, this.venue.timeZone, DateTime.DATETIME_SHORT)}`,
                    'Booking Updated'
                )
            })
    }

    showChangeBookingReasonOptions() {
        const reasonChoices = [new BookingReasonChoice(null)]
        const reasons = this.venue.reasonsReservableOnDate(this.booking.start)
        reasons.forEach(reason => {
            reasonChoices.push(new BookingReasonChoice(reason))
        })
        this.bookingReasonView.elementChoices = of(reasonChoices)
    }

    showChangeBookingEventOptions() {
        const events = [new BookingEventChoice(null)]
        this.venue.events.forEach(event => {
            events.push(new BookingEventChoice(event))
        })
        this.bookingEventView.elementChoices = of(events)
    }

    showChangeBookingEndTimeOptions() {
        const availableEndDates = this.findAvailableBookingEndTimes()
        if (availableEndDates.length === 0) {
            this.bookingTimeView.cancelElementUpdate()
            this.showError(
                'Couldn\'t load available booking end times. Please try again.',
                'Booking Update Error'
            )
        } else {
            this.bookingTimeView.elementChoices =
                of(availableEndDates.map(date => new TimeElementChoice(date, this.venue.timeZone, this.datePipe)))
        }
    }

    showChangePartySizeOptions() {
        if (!this.area) {
            return
        }
        const maxPartySize = 100
        const choices: PartySizeChoice[] = []
        for (let size = 1; size <= maxPartySize; size++) {
            choices.push(new PartySizeChoice(size))
        }
        this.partySizeView.elementChoices = of(choices)
    }

    showChangeBookingTableOptions() {
        if (!this.area) {
            return
        }
        const modal = this.modalService.open(TableSelectionComponent, {
            size: 'xl',
            scrollable: true,
            modalDialogClass: 'modal-md-block',
        })
        const currentTables = this.area.tablesUsedForBooking(this.booking)
        const component = modal.componentInstance as TableSelectionComponent
        component.currentlySelectedTables = currentTables
        component.venue = this.venue
        component.bookings = this.otherBookings
        component.partySize = this.booking.size
        component.startDate = this.booking.start
        component.endDate = this.booking.end
        const savingTableSelection = new Subject<boolean>()
        component.isSavingSelection = savingTableSelection.asObservable()
        component.tablesSelected
            .pipe(
                takeUntil(this.onDestroy),
                tap(_ => savingTableSelection.next(true)),
                delay(1000),
                finalize(() => savingTableSelection.next(false)),
                mergeMap((selectedTables: Table[]) => {
                    return this.requestChangeBookingTables(selectedTables)
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.booking = booking
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    const tableName = this.tableNamePipe.transform(
                        booking, this.area
                    )
                    this.showSuccess(
                        `Booking table changed to ${tableName}`
                    )
                },
                error: _ => {
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update the table for this booking.' +
                        'Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateBookingTime(time: Date) {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = { ...this.booking,
            duration: Helper.minutesBetweenDates(this.booking.start, time) } as Booking
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingTimeView.finishElementUpdate()
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        `Booking duration changed to ${booking.duration} minutes`,
                        'Booking Updated'
                    )
                },
                error: _ => {
                    this.bookingTimeView.cancelElementUpdate()
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s time. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateBookingReason(reason: BookingReason | null) {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.reasonId = reason?.id ?? null
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingReasonView.finishElementUpdate()
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        'Booking reason updated',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    this.bookingReasonView.cancelElementUpdate()
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s reason. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateBookingEvent(event: Event | null) {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.eventId = event?.id ?? null
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingEventView.finishElementUpdate()
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        'Booking event updated',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    this.bookingEventView.cancelElementUpdate()
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s event. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateCustomerMessage(message: string | null) {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.notes = message
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.customerMessageView.finishTextUpdate()
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        'Customer\'s Booking Notes updated',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    this.customerMessageView.cancelTextUpdate()
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update the customer\'s booking notes. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateMerchantNotes(merchantNotes: string | null): void {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = { ...this.booking,
            merchantNotes: merchantNotes } as Booking
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.merchantNotesView.finishTextUpdate()
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        'Your Booking Notes updated',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    this.merchantNotesView.cancelTextUpdate()
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update the booking\'s notes. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updatePartySize(partySize: number): void {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.updatePartySize(partySize)
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.partySizeView.finishElementUpdate()
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        'Booking Party Size updated',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    this.partySizeView.cancelElementUpdate()
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s size. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateRequiresWheelchairAccess(requiresWheelchairAccess: boolean): void {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.requiresWheelchairAccess = requiresWheelchairAccess
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        requiresWheelchairAccess ? 
                            'Wheelchair access requirement added' : 
                            'Wheelchair access requirement removed',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s accessibility requirements. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateRequiresDogFriendly(requiresDogFriendly: boolean): void {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.requiresDogFriendly = requiresDogFriendly
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        requiresDogFriendly ? 
                            'Dog friendly requirement added' : 
                            'Dog friendly requirement removed',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s accessibility requirements. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateRequiresPramAccessible(requiresPramAccessible: boolean): void {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.requiresPramAccessible = requiresPramAccessible
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        requiresPramAccessible ? 
                            'Pram accessible requirement added' : 
                            'Pram accessible requirement removed',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s accessibility requirements. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateRequiresHighchairFriendly(requiresHighchairFriendly: boolean): void {
        if (!this.area) {
            return
        }
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.requiresHighchairFriendly = requiresHighchairFriendly
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        requiresHighchairFriendly ?
                            'Highchair friendly requirement added' :
                            'Highchair friendly requirement removed',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking\'s accessibility requirements. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateTextQuestionAnswer(questionId: string, answer: string | null): void {
        if (!this.area) {
            return
        }
        const questionIndex = this.viewModel.textQuestions.findIndex(q => q.questionId === questionId)
        const inputComponent = this.textQuestionInputs?.toArray()[questionIndex]
        
        this.viewModel.isEditingBooking = true
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        const answerToUpdate = updatedBooking.answers.find(a => a.question.id === questionId)
        if (answerToUpdate) {
            answerToUpdate.answer = answer ?? ''
        }
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    inputComponent?.finishInputUpdate()
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        'Question answer updated',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    inputComponent?.cancelInputUpdate()
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update the question answer. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    toggleBookingLockedToTables() {
        if (!this.area) {
            return
        }
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.lockedToTables = !this.booking.lockedToTables
        this.viewModel.isEditingBooking = true
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingChanged.emit([booking, {
                        changeHasFinishedInteraction: false,
                    }])
                    this.showSuccess(
                        booking.lockedToTables ?
                            'Locked to tables' :
                            'Unlocked from tables',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    //TODO Track the error
                    this.showError(
                        'Couldn\'t update this booking. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    updateSendFeedbackRequest(sendFeedbackRequest: boolean): void {
        if (!this.area || this.booking.sendFeedbackRequest === sendFeedbackRequest) {
            return
        }
        const updatedBooking = this.modelCloningService.cloneBooking(this.booking)
        updatedBooking.sendFeedbackRequest = sendFeedbackRequest
        this.viewModel.isEditingBooking = true
        this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.viewModel.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.booking = booking
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                    this.showSuccess(
                        'Feedback request updated',
                        'Booking Updated'
                    )
                },
                error: _ => {
                    this.showError(
                        'Couldn\'t update this booking\'s feedback request. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    goToCustomer() {
        const customer = this.booking.customer
        if (customer === null) {
            return
        }
        this.customerSelected.emit(customer)
    }

    private findAvailableBookingEndTimes(): Date[] {
        const organisation = this.contextService.getOrganisation()
        if (organisation === null) {
            return []
        }
        const area = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return []
        }
        return organisation.unoccupiedEndTimesForBookingInVenue(
            this.venue,
            this.booking,
            this.otherBookings
        )
    }

    private requestChangeBookingTables(selectedTables: Table[]): Observable<Booking> {
        if (!this.area) {
            return of(this.booking)
        }
        const updatedBooking = {
            ...this.booking,
            tableIds: selectedTables.map(table => table.id),
        } as Booking
        return this.bookingService.patchBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            this.booking,
            updatedBooking
        )
            .pipe(
                takeUntil(this.onDestroy)
            )
    }

    private showError(message: string, title?: string) {
        this.showToast(ToastType.Danger, message, title)
    }

    private showSuccess(message: string, title?: string) {
        this.showToast(ToastType.Success, message, title)
    }

    private showToast(toastType: ToastType, message: string, title?: string) {
        let  toastBuilder = new ToastBuilder(message)
            .withType(toastType)

        if (title){
            toastBuilder = toastBuilder.withHeader(title)
        }

        this.toastService.show(toastBuilder.build())
    }

    protected readonly InputType = InputType
    protected readonly DateTime = DateTime
}
